import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
    Table,
  TableCell,
  TableRow,
  TableBody,
    // TableCell,
    TableContainer,
    TableHead,
    TablePagination,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useParams } from "react-router-dom";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import axios from "axios";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  UIButton,
  UIPageHeader,
  UIStatusChip,
  UITableActionCell,
  UITableFooter,
  UITableShell,
  UITableToolbar,
} from "../../../../components/ui";
import {
  buildNewPasswordSchema,
  buildOptionalNewPasswordSchema,
} from "../../../../helpers/passwordValidation";
import "./InstanceUsers.css";
import ProfileAutocomplete from "../../../profile/ProfileAutocomplete";

import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import Snackbar from "@mui/material/Snackbar";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  createTeam,
  deactivateTeam,
  fetchTeams,
  updateTeam,
} from "../../../../services/teams";

const USER_ROLE_OPTIONS = [
  { value: "purchasing-rep", label: "Purchasing Rep" },
  { value: "production-rep", label: "Production Rep" },
  { value: "order-rep", label: "Order Rep" },
  { value: "client-rep", label: "Client Rep" },
  { value: "shipping-rep", label: "Shipping Rep" },
  { value: "finance-rep", label: "Finance Rep" },
  { value: "design-rep", label: "Design Rep" },
  { value: "operations-rep", label: "Operations Rep" },
  { value: "administrator", label: "Administrator" },
  { value: "super-administrator", label: "Super Administrator" },
];

const USERS_COLUMNS = [
  {
    id: "profile",
    label: "Profile",
    minWidth: 220,
    align: "left",
    sortable: false,
  },
  { id: "id", label: "User ID", minWidth: 96, align: "left", sortable: true },
  {
    id: "first_name",
    label: "First Name",
    minWidth: 120,
    align: "left",
    sortable: true,
  },
  {
    id: "last_name",
    label: "Last Name",
    minWidth: 120,
    align: "left",
    sortable: true,
  },
  { id: "email", label: "Email", minWidth: 240, align: "left", sortable: true },
  { id: "role", label: "Role", minWidth: 150, align: "left", sortable: true },
  {
    id: "hourly_rate",
    label: "Hourly Rate",
    minWidth: 132,
    align: "left",
    sortable: true,
  },
  {
    id: "actions",
    label: "Actions",
    minWidth: 92,
    align: "right",
    sortable: false,
    locked: true,
  },
];

const USERS_EXPORT_COLS = Object.freeze([
  { id: "id", label: "User ID" },
  { id: "first_name", label: "First Name" },
  { id: "last_name", label: "Last Name" },
  { id: "email", label: "Email" },
  { id: "role", label: "Role" },
  { id: "hourly_rate", label: "Hourly Rate" },
  { id: "hourly_rate_currency", label: "Currency" },
]);

const TEAMS_COLUMNS = [
  { id: "id", label: "Team ID", minWidth: 88, align: "left" },
  { id: "key", label: "Key", minWidth: 140, align: "left" },
  { id: "name", label: "Team Name", minWidth: 200, align: "left" },
  { id: "type", label: "Type", minWidth: 120, align: "left" },
  { id: "description", label: "Description", minWidth: 280, align: "left" },
  { id: "members", label: "Members", minWidth: 260, align: "left" },
  { id: "active", label: "Active", minWidth: 96, align: "left" },
  { id: "actions", label: "Actions", minWidth: 180, align: "right" },
];

const formatRoleLabel = (role) => {
  const raw = String(role || "").trim();
  if (!raw) return "Unassigned";
  if (["super-administrator", "elite-core-operator", "elite-core-platform-administrator"].includes(raw.toLowerCase().replace(/[_\s]+/g, "-"))) return "Super Administrator";
  if (["administrator", "admin"].includes(raw.toLowerCase().replace(/[_\s]+/g, "-"))) return "Administrator";
  return raw
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const formatEffectiveRoleLabel = (user) => {
  const platformRole = String(user?.platformRole || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  if (platformRole === 'elite-core-operator' || platformRole === 'elite-core-platform-administrator') {
    return 'Super Administrator';
  }
  return formatRoleLabel(user?.role);
};

const getUserInitials = (user) => {
  const first = String(user?.first_name || "")
    .trim()
    .charAt(0);
  const last = String(user?.last_name || "")
    .trim()
    .charAt(0);
  return `${first}${last}`.trim().toUpperCase() || "U";
};

const resolveProfileImage = (profilePic) => {
  if (!profilePic) return "";

  if (typeof profilePic !== "string") {
    if (Array.isArray(profilePic) && profilePic.length > 0) {
      const selected = profilePic.find((pic) => pic?.selected) || profilePic[0];
      if (selected?.url) {
        return selected.url.startsWith("uploads")
          ? `${process.env.REACT_APP_SERVER_URL}/${selected.url}`
          : selected.url;
      }
    }
    return "";
  }

  try {
    const parsed = JSON.parse(profilePic);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const selected = parsed.find((pic) => pic?.selected) || parsed[0];
      if (selected?.url) {
        return selected.url.startsWith("uploads")
          ? `${process.env.REACT_APP_SERVER_URL}/${selected.url}`
          : selected.url;
      }
    }
  } catch (_) {
    return profilePic.startsWith("uploads")
      ? `${process.env.REACT_APP_SERVER_URL}/${profilePic}`
      : profilePic;
  }

  return "";
};

const getUserPhoneValue = (user) =>
  user?.phone_number || user?.phoneNumber || user?.phone || "";

const formatHourlyRate = (value, currency = "CAD") => {
  if (value == null || value === "") return null;
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) return null;

  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const normalizeTeam = (team) => ({
  id: team?.id ?? null,
  key: String(team?.key || "").trim(),
  name: String(team?.name || "").trim(),
  description: String(team?.description || "").trim(),
  type: String(team?.type || "sales").trim() || "sales",
  memberUserIds: Array.isArray(team?.memberUserIds)
    ? team.memberUserIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
    : [],
  members: Array.isArray(team?.members) ? team.members : [],
  memberCount:
    Number(
      team?.memberCount ?? (Array.isArray(team?.members) ? team.members.length : 0)
    ) || 0,
  isActive: team?.isActive !== false,
});

const ROUTING_TEAM_ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "recipient", label: "Recipient" },
  { value: "manager", label: "Manager" },
];

const getMemberDisplayName = (member) => {
  const fullName = [
    member?.first_name || member?.firstName,
    member?.last_name || member?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || member?.email || "Unknown member";
};

const getMemberEmail = (member) => String(member?.email || "").trim();

const getMemberInitials = (member) => {
  const first = String(member?.first_name || member?.firstName || "")
    .trim()
    .charAt(0);
  const last = String(member?.last_name || member?.lastName || "")
    .trim()
    .charAt(0);

  if (first || last) {
    return `${first}${last}`.trim().toUpperCase();
  }

  const email = getMemberEmail(member);
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }

  return "?";
};

const getMemberAvatarSrc = (member) =>
  resolveProfileImage(
    member?.profilePicture ||
      member?.profile_picture ||
      member?.avatar ||
      member?.avatarUrl ||
      member?.image ||
      ""
  );

const getTeamMemberLabel = (member) => {
  const name = getMemberDisplayName(member);
  const email = getMemberEmail(member);
  if (name && email && name !== email) return `${name} (${email})`;
  return name || email || "Unknown member";
};

const filterUsers = (users, query) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return users;

  return users.filter((user) => {
    const fields = [
      user?.id,
      user?.first_name,
      user?.last_name,
      user?.email,
      user?.role,
    ];
    return fields.some((field) =>
      String(field || "")
        .toLowerCase()
        .includes(normalized),
    );
  });
};

const filterTeams = (teams, query) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return teams;

  return teams.filter((team) => {
    const fields = [
      team?.id,
      team?.key,
      team?.name,
      team?.type,
      team?.description,
      team?.memberCount,
      ...(Array.isArray(team?.members)
        ? team.members.map(
            (member) =>
              `${member?.first_name || ""} ${member?.last_name || ""} ${member?.email || ""}`.trim()
          )
        : []),
    ];
    return fields.some((field) =>
      String(field || "")
        .toLowerCase()
        .includes(normalized),
    );
  });
};

const usersValidationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().required("Phone number is required"),
  role: Yup.string().required("Role is required"),
  password: buildOptionalNewPasswordSchema(),
  hourly_rate: Yup.number()
    .typeError("Must be a number")
    .min(0)
    .nullable()
    .notRequired(),
  hourly_rate_currency: Yup.string().notRequired(),
});

const createUserValidationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().when("generateTemporaryPassword", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: () => buildNewPasswordSchema(),
  }),
  phone: Yup.string().required("Phone number is required"),
  role: Yup.string().required("Role is required"),
  hourly_rate: Yup.number()
    .typeError("Must be a number")
    .min(0)
    .nullable()
    .notRequired(),
  hourly_rate_currency: Yup.string().notRequired(),
  sendAccountCreatedEmail: Yup.boolean(),
  generateTemporaryPassword: Yup.boolean(),
});

const FormField = ({ name, label, helperText, ...props }) => {
  const [field, meta] = useField(name);

  return (
    <TextField
      {...field}
      label={label}
      fullWidth
      size="small"
      error={meta.touched && Boolean(meta.error)}
      helperText={meta.touched && meta.error ? meta.error : helperText}
      {...props}
    />
  );
};

const MetricCard = ({ icon, label, value, hint }) => (
  <Box className="instance-users-metric-card">
    <Box className="instance-users-metric-icon">{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography className="instance-users-metric-label">{label}</Typography>
      <Typography className="instance-users-metric-value">{value}</Typography>
      <Typography className="instance-users-metric-hint">{hint}</Typography>
    </Box>
  </Box>
);

const UserDialogShell = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  formProps, 
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.eliteCore.shadows.overlay,
        },
      }}
    >
      <DialogTitle
        className="instance-users-dialog-title"
        sx={{ px: { xs: 2.5, sm: 3 }, pt: { xs: 2.5, sm: 2.75 }, pb: 1.5 }}
      >
        <Typography variant="h6" sx={{ fontSize: 20 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: 13 }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent
        className="instance-users-dialog-content"
        sx={{ px: { xs: 2.5, sm: 3 }, py: 2.25 }}
      >
        {children}
      </DialogContent>
      <DialogActions
        className="instance-users-dialog-actions"
        sx={{ px: { xs: 2.5, sm: 3 }, pb: 2.5, pt: 1.25 }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  );
};
const EditUserModal = ({
  open,
  onClose,
  user,
  authState,
  onSuccess,
  instanceUniqueId,
  availableUsers = [],
}) => {
  const theme = useTheme();
  const [error, setError] = useState("");
  const [profilePictureValue, setProfilePictureValue] = useState(user?.profilePicture || "");
  const [profileUploadSubmitting, setProfileUploadSubmitting] = useState(false);
  const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
  const [preferredOrderReps, setPreferredOrderReps] = useState([]);
  const [preferredPurchasingReps, setPreferredPurchasingReps] = useState([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [bulkSource, setBulkSource] = useState("");
  const [bulkOperation, setBulkOperation] = useState("add");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkJobs, setBulkJobs] = useState([]);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveMode, setArchiveMode] = useState("keep");
  const [archiveReplacementUserId, setArchiveReplacementUserId] = useState("");
  const [archiveNote, setArchiveNote] = useState("");
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const normalizeRole = (role) => String(role || "").trim().toLowerCase().replace(/[_\s]+/g, "-");
  const representativeRole = (member) => normalizeRole(member?.globalRole || member?.role);
  const archiveStatus = normalizeRole(user?.archiveStatus);
  const isArchiving = archiveStatus === "archiving";
  const archiveFailed = archiveStatus === "failed";
  const isAdministrator = ["administrator", "admin", "super-administrator"].includes(normalizeRole(authState?.role));
  const canEditPreferences = isAdministrator &&
    ["order-rep", "client-rep", "administrator", "super-administrator", "superadmin"].includes(normalizeRole(user?.role));
  const activeReplacementUsers = availableUsers.filter(
    (member) => Number(member?.id) !== Number(user?.id) && member?.archived !== true
  );

  const selectedBulkUser = activeReplacementUsers.find(member => Number(member.id) === Number(bulkSource));
  const selectedBulkName = selectedBulkUser
    ? ([selectedBulkUser.first_name, selectedBulkUser.last_name].filter(Boolean).join(" ").trim() || selectedBulkUser.email || "this user")
    : "the selected user";

  useEffect(() => {
    if (!open || !canEditPreferences || !user?.id) return;
    let active = true;
    setPreferencesLoading(true);
    setPreferencesReady(false);
    setPreferredOrderReps([]);
    setPreferredPurchasingReps([]);
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/basicinfo/${user.id}`, {
      headers: { accessToken: authState?.token || localStorage.getItem("accessToken") },
    }).then(({ data }) => {
      if (!active) return;
      let entries = data.recommendedUsers || [];
      if (typeof entries === "string") entries = JSON.parse(entries);
      if (!Array.isArray(entries)) throw new Error("Invalid representative preferences");
      setPreferredOrderReps(entries.filter(entry => entry.role === "order-rep").map(entry => Number(entry.id)));
      setPreferredPurchasingReps(entries.filter(entry => entry.role === "purchasing-rep").map(entry => Number(entry.id)));
      setPreferencesReady(true);
    }).catch(() => {
      if (active) setError("Unable to load preferred representatives. Close and reopen this popup to retry.");
    }).finally(() => {
      if (active) setPreferencesLoading(false);
    });
    return () => { active = false; };
  }, [open, user?.id, canEditPreferences, authState?.token]);

  useEffect(() => {
    if (!open) return;
    setProfilePictureValue(user?.profilePicture || "");
    setError("");
  }, [open, user]);

  useEffect(() => {
    if (!open || !isAdministrator || !user?.id || !instanceUniqueId) return;
    let active = true;
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/bulk-assignments`, {
      params: { instanceUniqueId },
      headers: { accessToken: authState?.token || localStorage.getItem("accessToken") }
    }).then(({ data }) => { if (active) setBulkJobs(data.jobs || []); }).catch(() => {});
    return () => { active = false; };
  }, [open, isAdministrator, user?.id, instanceUniqueId, authState?.token]);

  if (!user) return null;

  const avatarSrc = resolveProfileImage(profilePictureValue);
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "User";

  const refreshAfterProfileChange = async () => {
    if (typeof onSuccess === "function") {
      await onSuccess(null, "Profile picture updated successfully.");
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      setError("Please upload a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Please upload an image smaller than 2MB.");
      return;
    }

    setError("");
    setProfileUploadSubmitting(true);

    try {
      const data = new FormData();
      data.append("profilePicture", file);
      data.append("instanceUniqueId", instanceUniqueId || "");

      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/profile-picture`,
        data,
        {
          headers: {
            accessToken:
              authState?.token || localStorage.getItem("accessToken"),
          },
        }
      );

      const nextProfilePicture =
        response?.data?.profilePicture || response?.data || [];
      setProfilePictureValue(nextProfilePicture);
      await refreshAfterProfileChange();
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.error || "Unable to upload profile picture"
      );
    } finally {
      setProfileUploadSubmitting(false);
    }
  };

  const handleGeneratePassword = async () => {
    const confirmed = window.confirm(
      `Generate a new temporary password for ${displayName} and email it to ${user.email}? This will overwrite their current password.`
    );
    if (!confirmed) return;

    setError("");
    setPasswordResetSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/generate-password`,
        { instanceUniqueId },
        {
          headers: {
            accessToken:
              authState?.token || localStorage.getItem("accessToken"),
          },
        }
      );

      if (typeof onSuccess === "function") {
        await onSuccess(null, "Temporary password generated and emailed to user.");
      }
    } catch (resetError) {
      setError(
        resetError?.response?.data?.error ||
          "Unable to generate and email password."
      );
    } finally {
      setPasswordResetSubmitting(false);
    }
  };

  const loadBulkJobs = async () => {
    if (!isAdministrator || !user?.id || !instanceUniqueId) return;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/bulk-assignments`,
        { params: { instanceUniqueId }, headers: { accessToken: authState?.token || localStorage.getItem("accessToken") } }
      );
      setBulkJobs(data.jobs || []);
    } catch (err) { setError(err?.response?.data?.error || "Unable to load assignment jobs."); }
  };
  const submitBulk = async () => {
    if (!bulkSource || Number(bulkSource) === Number(user.id)) return;
    const sourceName = (() => { const entry = availableUsers.find(member => Number(member.id) === Number(bulkSource)); return entry ? ([entry.first_name, entry.last_name].filter(Boolean).join(" ") || entry.email) : "the selected user"; })();
    const actionText = bulkOperation === "add"
      ? `Add ${displayName} to all customers assigned to ${sourceName}?`
      : bulkOperation === "replace"
        ? `Replace ${sourceName} with ${displayName} on those customer accounts?`
        : `Remove ${displayName} from customers also assigned to ${sourceName}?`;
    if (!window.confirm(`${actionText}\n\nOther users' assignments are preserved. This runs in the background.`)) return;
    setBulkSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/bulk-assignments`,
        { instanceUniqueId, sourceUserId: Number(bulkSource), operation: bulkOperation, scope: bulkOperation === "remove" ? "shared" : "source" },
        { headers: { accessToken: authState?.token || localStorage.getItem("accessToken") } }
      );
      await loadBulkJobs();
    } catch (err) { setError(err?.response?.data?.error || "Unable to start assignment job."); }
    finally { setBulkSubmitting(false); }
  };

  const beginArchive = () => {
    if (!isAdministrator || user?.archived || isArchiving) return;
    const confirmed = window.confirm(
      `Archive ${displayName} from this Elite Core instance?\n\nThey will no longer be available for new assignments. Historical records will remain unchanged.`
    );
    if (!confirmed) return;
    setArchiveMode("keep");
    setArchiveReplacementUserId("");
    setArchiveNote("");
    setArchiveDialogOpen(true);
  };

  const submitArchive = async () => {
    if (archiveMode === "reassign" && !archiveReplacementUserId) {
      setError("Choose a replacement user before archiving.");
      return;
    }
    setArchiveSubmitting(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/archive-instance`,
        {
          instanceUniqueId,
          mode: archiveMode,
          replacements: archiveMode === "reassign"
            ? { default: Number(archiveReplacementUserId) }
            : {},
          note: archiveNote,
        },
        {
          headers: {
            accessToken: authState?.token || localStorage.getItem("accessToken"),
          },
        }
      );
      setArchiveDialogOpen(false);
      onClose();
      await onSuccess(
        response?.data || null,
        response?.data?.archiveStatus === "archiving"
          ? "Archive started. Customer assignments will update safely in the background."
          : "User archived successfully."
      );
    } catch (archiveError) {
      setError(archiveError?.response?.data?.error || "Unable to archive user.");
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const restoreUser = async () => {
    if (!isAdministrator || !user?.archived) return;
    const confirmed = window.confirm(`Restore ${displayName} to this Elite Core instance?`);
    if (!confirmed) return;
    setArchiveSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${user.id}/restore-instance`,
        { instanceUniqueId },
        {
          headers: {
            accessToken: authState?.token || localStorage.getItem("accessToken"),
          },
        }
      );
      onClose();
      await onSuccess(null, "User restored successfully.");
    } catch (restoreError) {
      setError(restoreError?.response?.data?.error || "Unable to restore user.");
    } finally {
      setArchiveSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone: getUserPhoneValue(user),
        role: user.role || "",
        customUploadedBrandLogo: user.profile_pic || "",
        password: "",
        hourly_rate: user.hourly_rate ?? "",
        hourly_rate_currency: user.hourly_rate_currency || "CAD",
      }}
      validationSchema={usersValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setError("");
        try {
          const payload = {
            ...values,
            instanceUniqueId,
            profile_pic: values.customUploadedBrandLogo,
            // Role edits must also update the role stored in this instance's userAccess.
            instanceUniqueId,
          };
          if (canEditPreferences && ["order-rep", "client-rep", "administrator", "super-administrator", "superadmin"].includes(normalizeRole(values.role))) {
            if (!preferencesReady) throw new Error("Preferred representatives have not loaded.");
            payload.instanceUniqueId = instanceUniqueId;
            payload.orderRepIds = preferredOrderReps;
            payload.purchasingRepIds = preferredPurchasingReps;
          }

          await axios.put(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/update-user/${user.id}`,
            { data: payload },
            {
              headers: {
                accessToken:
                  authState?.token || localStorage.getItem("accessToken"),
              },
            }
          );

          onSuccess(null, "User updated successfully.", "success");
          onClose();
        } catch (err) {
          setError(err?.response?.data?.error || "Unable to update user");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, submitForm, values }) => (
        <Form id="edit-user-form">
          <UserDialogShell
            open={open}
            onClose={onClose}
            title="Edit user"
            subtitle="Update account details and access for this team member."
            actions={
              <>
                {isAdministrator && (
                  <Button
                    color={user?.archived && !isArchiving ? "success" : "error"}
                    variant="outlined"
                    disabled={archiveSubmitting || isArchiving}
                    onClick={user?.archived && !isArchiving ? restoreUser : beginArchive}
                    sx={{ mr: "auto" }}
                  >
                    {isArchiving ? "Archiving..." : user?.archived ? "Restore user" : "Archive user"}
                  </Button>
                )}
                <Button onClick={onClose} color="inherit">
                  Cancel
                </Button>
                <UIButton
                  // type="submit"
                  variant="contained"
                  form="edit-user-form"
                  disabled={isSubmitting || (canEditPreferences && !preferencesReady)}
                  onClick={submitForm}
                >
                  {isSubmitting ? "Saving..." : "Save changes"}
                </UIButton>
              </>
            }
          >
            <Box className="instance-users-dialog-stack">
              {isArchiving ? (
                <Alert severity="info">
                  <strong>Archiving user.</strong> They are already excluded from new assignments while Elite Core processes customer assignments in small background batches.
                  {Number(user?.archiveJob?.scannedCustomers || 0) > 0
                    ? ` Scanned ${Number(user.archiveJob.scannedCustomers).toLocaleString()} customers; updated ${Number(user.archiveJob.updatedCustomers || 0).toLocaleString()} assignments so far.`
                    : ""}
                  {user?.archiveJob?.lastError ? ` Last retry issue: ${user.archiveJob.lastError}` : ""}
                </Alert>
              ) : archiveFailed ? (
                <Alert severity="error">
                  <strong>Archive did not finish.</strong> The user remains active for this instance. You can correct the issue and archive them again.
                  {user?.archiveJob?.lastError ? ` Last error: ${user.archiveJob.lastError}` : ""}
                </Alert>
              ) : user?.archived ? (
                <Alert severity="warning">
                  <strong>Archived user.</strong> This user remains on historical records but is excluded from active assignment lists for this instance.
                  {user?.archivedAt ? ` Archived ${new Date(user.archivedAt).toLocaleString()}.` : ""}
                  {user?.archiveNote ? ` Note: ${user.archiveNote}` : ""}
                </Alert>
              ) : null}
              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Profile
                </Typography>
                <Box className="instance-users-dialog-grid">
                  <FormField name="first_name" label="First name" />
                  <FormField name="last_name" label="Last name" />
                  <FormField name="email" label="Email" type="email" />
                  <FormField name="phone" label="Phone" />
                </Box>
              </Box>

              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Profile Picture
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={avatarSrc}
                      sx={{
                        width: 72,
                        height: 72,
                        fontSize: 24,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      }}
                    >
                      {getUserInitials(user)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography className="instance-users-cell-primary">
                        {displayName}
                      </Typography>
                      <Typography className="instance-users-cell-secondary">
                        {user?.email || "No email"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Upload a profile picture for this user. This uses the same profile picture system as the user profile page.
                  </Typography>

                  <Box>
                    <input
                      id={`edit-user-profile-picture-${user.id}`}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={handleProfilePictureUpload}
                    />
                    <label htmlFor={`edit-user-profile-picture-${user.id}`}>
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        disabled={profileUploadSubmitting}
                      >
                        {profileUploadSubmitting
                          ? "Uploading..."
                          : "Upload profile picture"}
                      </Button>
                    </label>
                  </Box>
                </Stack>
              </Box>


              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Access and pay
                </Typography>
                <Box className="instance-users-dialog-grid">
                  <FormField name="role" label="Role" select>
                    {USER_ROLE_OPTIONS.filter((option) =>
                      option.value !== "super-administrator" ||
                      normalizeRole(authState?.role) === "super-administrator" ||
                      normalizeRole(user?.role) === "super-administrator"
                    ).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </FormField>
                  <FormField
                    name="password"
                    label="Password"
                    type="password"
                    helperText="Leave blank to keep the current password."
                  />
                  <FormField name="hourly_rate" label="Hourly rate" type="number" />
                  <FormField name="hourly_rate_currency" label="Currency" />
                </Box>
              </Box>

              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Password Reset
                </Typography>
                <Stack spacing={1.25} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    Generate a new temporary password and email it to this user.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={passwordResetSubmitting}
                    onClick={handleGeneratePassword}
                  >
                    {passwordResetSubmitting
                      ? "Generating..."
                      : "Generate new password"}
                  </Button>
                </Stack>
              </Box>

              {canEditPreferences && ["order-rep", "client-rep", "administrator", "super-administrator", "superadmin"].includes(normalizeRole(values.role)) ? (
                <Box className="instance-users-dialog-panel">
                  <Typography className="instance-users-dialog-section-label">
                    Preferred representatives
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manage the same preferred representatives shown on this user's profile.
                  </Typography>
                  <Stack spacing={2}>
                    <ProfileAutocomplete
                      multiple
                      size="small"
                      options={availableUsers.filter(member => representativeRole(member) === "order-rep").map(member => Number(member.id))}
                      value={preferredOrderReps}
                      onChange={(_event, ids) => setPreferredOrderReps(ids)}
                      getOptionLabel={id => {
                        const member = availableUsers.find(entry => Number(entry.id) === id);
                        return member ? `${member.first_name} ${member.last_name}`.trim() || member.email : `Unavailable user #${id}`;
                      }}
                      disabled={isSubmitting || preferencesLoading || !preferencesReady}
                      renderInput={params => <TextField {...params} label="Preferred order reps" helperText="Select one or more order reps, or clear to remove." />}
                    />
                    <ProfileAutocomplete
                      multiple
                      size="small"
                      options={availableUsers.filter(member => representativeRole(member) === "purchasing-rep").map(member => Number(member.id))}
                      value={preferredPurchasingReps}
                      onChange={(_event, ids) => setPreferredPurchasingReps(ids)}
                      getOptionLabel={id => {
                        const member = availableUsers.find(entry => Number(entry.id) === id);
                        return member ? `${member.first_name} ${member.last_name}`.trim() || member.email : `Unavailable user #${id}`;
                      }}
                      disabled={isSubmitting || preferencesLoading || !preferencesReady}
                      renderInput={params => <TextField {...params} label="Preferred purchasing reps" helperText="Select one or more purchasing reps, or clear to remove." />}
                    />
                  </Stack>
                </Box>
              ) : null}

              {isAdministrator && (
                <Box className="instance-users-dialog-panel">
                  <Typography variant="h6">Manage {displayName}'s customer assignments</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add or remove {displayName} from customer accounts connected to another team member.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField select fullWidth size="small" label="Compare with another team member" value={bulkSource}
                      onChange={event => setBulkSource(event.target.value)}>
                      {activeReplacementUsers.map(member =>
                        <MenuItem key={member.id} value={String(member.id)}>
                          {[member.first_name, member.last_name].filter(Boolean).join(" ") || member.email}
                        </MenuItem>
                      )}
                    </TextField>
                    <TextField select fullWidth size="small" label={`What should happen to ${displayName}?`} value={bulkOperation}
                      onChange={event => setBulkOperation(event.target.value)}>
                      <MenuItem value="add">Add {displayName} to {selectedBulkName}'s customers</MenuItem>
                      <MenuItem value="remove">Remove {displayName} from customers shared with {selectedBulkName}</MenuItem>
                      <MenuItem value="replace">Replace {selectedBulkName} with {displayName}</MenuItem>
                    </TextField>
                    <Alert severity="info">
                      {!bulkSource
                        ? "Choose another team member to see what will change."
                        : bulkOperation === "add"
                          ? `Add ${displayName} to every customer assigned to ${selectedBulkName}. Keep ${selectedBulkName} and everyone else assigned. Skip customers already assigned to ${displayName}.`
                          : bulkOperation === "replace"
                            ? `For customers assigned to ${selectedBulkName}, remove ${selectedBulkName} and assign ${displayName} instead. Any other assigned team members stay.`
                            : `Remove ${displayName} only where the customer is assigned to both ${displayName} and ${selectedBulkName}. ${selectedBulkName} and any other assigned team members stay. Customers assigned only to ${displayName} are not changed.`}
                    </Alert>
                    <Button variant="contained" disabled={!bulkSource || bulkSubmitting}
                      onClick={submitBulk}>{bulkSubmitting ? "Starting..." : "Apply to customers"}</Button>
                    <Button variant="text" onClick={loadBulkJobs}>Refresh progress</Button>
                    {bulkJobs.map(job => <Typography variant="body2" key={job.id}>
                      Job #{job.id}: {job.operation} — {job.status}; scanned {job.scanned}, matched {job.matched}, changed {job.changed}
                      {job.lastError ? ` — ${job.lastError}` : ""}
                    </Typography>)}
                  </Stack>
                </Box>
              )}
            </Box>

            {error ? (
              <Alert severity="error" sx={{ mt: 2.25 }}>
                {error}
              </Alert>
            ) : null}
          </UserDialogShell>

          <Dialog
            open={archiveDialogOpen}
            onClose={() => !archiveSubmitting && setArchiveDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Customer assignments</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Alert severity="info">
                  Choose what should happen to this user's current customer assignments. Historical projects, orders, commissions, notes, and purchase orders are not changed.
                </Alert>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Customer assignment action"
                  value={archiveMode}
                  onChange={(event) => setArchiveMode(event.target.value)}
                >
                  <MenuItem value="keep">Keep existing assignments and show Archived</MenuItem>
                  <MenuItem value="reassign">Reassign their customer positions to another user</MenuItem>
                  <MenuItem value="remove">Remove them from customer assignments</MenuItem>
                </TextField>
                {archiveMode === "reassign" && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Replacement user"
                    value={archiveReplacementUserId}
                    onChange={(event) => setArchiveReplacementUserId(event.target.value)}
                    helperText="The replacement keeps the same customer position (for example Client Rep stays Client Rep)."
                  >
                    {activeReplacementUsers.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {[member?.first_name, member?.last_name].filter(Boolean).join(" ") || member?.email}
                        {" — "}{formatRoleLabel(member?.role)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Archive note (optional)"
                  value={archiveNote}
                  onChange={(event) => setArchiveNote(event.target.value)}
                  multiline
                  minRows={2}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveSubmitting}>
                Back
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={submitArchive}
                disabled={archiveSubmitting || (archiveMode === "reassign" && !archiveReplacementUserId)}
              >
                {archiveSubmitting ? "Archiving..." : "Archive user"}
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};
const CreateUserModal = ({
  open,
  onClose,
  authState,
  instanceUniqueId,
  onSuccess,
}) => {
  const [error, setError] = useState("");

  return (
    <Formik
      initialValues={{
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        phone: "",
        role: "",
        customUploadedBrandLogo: "",
        hourly_rate: "",
        hourly_rate_currency: "CAD",
        sendAccountCreatedEmail: false,
        generateTemporaryPassword: false,
      }}
      validationSchema={createUserValidationSchema}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        setError("");
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/create-new-user`,
            {
              data: {
                ...values,
                instanceUniqueId,
                profile_pic: values.customUploadedBrandLogo,
              },
            },
            {
              headers: {
                accessToken: authState?.token || localStorage.getItem("accessToken"),
              },
            }
          );

          resetForm();
          onSuccess(response?.data || null);
          onClose();
        } catch (err) {
          setError(err?.response?.data?.error || "Unable to create user");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, submitForm, values, setFieldValue }) => (
        <Form>
          <UserDialogShell
            open={open}
            onClose={onClose}
            title="Create user"
            subtitle="Add a new user and assign their role and hourly rate."
            actions={
              <>
                <Button onClick={onClose} color="inherit">
                  Cancel
                </Button>
                <UIButton
                  // type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={submitForm}
                >
                  {isSubmitting ? "Creating..." : "Create user"}
                </UIButton>
              </>
            }
          >
            <Box className="instance-users-dialog-stack">
              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Profile
                </Typography>
                <Box className="instance-users-dialog-grid">
                  <FormField name="first_name" label="First name" />
                  <FormField name="last_name" label="Last name" />
                  <FormField name="email" label="Email" type="email" />
                  <FormField name="phone" label="Phone" />
                </Box>
              </Box>

              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Access and pay
                </Typography>
                <Box className="instance-users-dialog-grid">
                  <FormField name="role" label="Role" select>
                    {USER_ROLE_OPTIONS.filter((option) =>
                      option.value !== "super-administrator" ||
                      String(authState?.role || "").toLowerCase().replace(/[_\s]+/g, "-") === "super-administrator"
                    ).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </FormField>
                  <FormField
                    name="password"
                    label="Password"
                    type="password"
                    helperText={
                      values.generateTemporaryPassword
                        ? "Leave blank when generating a temporary password."
                        : undefined
                    }
                    disabled={values.generateTemporaryPassword}
                  />
                  <FormField name="hourly_rate" label="Hourly rate" type="number" />
                  <FormField name="hourly_rate_currency" label="Currency" />
                </Box>
              </Box>

              <Box className="instance-users-dialog-panel">
                <Typography className="instance-users-dialog-section-label">
                  Invite options
                </Typography>
                <Stack spacing={1.25}>
                  <CheckboxFieldRow
                    checked={values.sendAccountCreatedEmail}
                    onChange={(checked) => {
                      setFieldValue("sendAccountCreatedEmail", checked);
                      if (!checked) {
                        setFieldValue("generateTemporaryPassword", false);
                      }
                    }}
                    label="Send user an email that their account was created"
                    helperText="Use this when inviting a new user into Elite Core."
                  />

                  {values.sendAccountCreatedEmail ? (
                    <CheckboxFieldRow
                      checked={values.generateTemporaryPassword}
                      onChange={(checked) =>
                        setFieldValue("generateTemporaryPassword", checked)
                      }
                      label="Generate a temporary password and include it in the email"
                      helperText="A temporary password will be generated and included in the email. The user should change it after logging in."
                    />
                  ) : null}
                </Stack>
              </Box>
            </Box>

            {error ? (
              <Alert severity="error" sx={{ mt: 2.25 }}>
                {error}
              </Alert>
            ) : null}
          </UserDialogShell>
        </Form>
      )}
    </Formik>
  );
};

function CheckboxFieldRow({ checked, onChange, label, helperText }) {
  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          sx={{ mt: -0.5 }}
        />
        <Box sx={{ pt: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {helperText ? (
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

const TeamDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  users = [],
  team = null,
}) => {
  const [values, setValues] = useState({
    key: "",
    name: "",
    description: "",
    type: "sales",
    memberUserIds: [],
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setValues({
      key: team?.key || "",
      name: team?.name || "",
      description: team?.description || "",
      type: team?.type || "sales",
      memberUserIds: Array.isArray(team?.memberUserIds) ? team.memberUserIds.map(Number) : [],
    });
    setError("");
  }, [open, team]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((left, right) =>
        `${left?.first_name || ""} ${left?.last_name || ""}`
          .trim()
          .localeCompare(`${right?.first_name || ""} ${right?.last_name || ""}`.trim())
      ),
    [users]
  );

  const handleSubmit = async () => {
    if (!String(values.name || "").trim()) {
      setError("Team name is required.");
      return;
    }

    setError("");
    await onSubmit({
      key: String(values.key || "").trim(),
      name: String(values.name || "").trim(),
      description: String(values.description || "").trim(),
      type: String(values.type || "sales").trim() || "sales",
      memberUserIds: Array.isArray(values.memberUserIds)
        ? values.memberUserIds.map(Number).filter((id) => Number.isFinite(id))
        : [],
    });
  };

  return (
    <UserDialogShell
      open={open}
      onClose={onClose}
      title={team?.id ? "Edit team" : "Create team"}
      subtitle="Create a lightweight team and assign users from this instance."
      actions={
        <>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <UIButton variant="contained" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Saving..." : team?.id ? "Save team" : "Create team"}
          </UIButton>
        </>
      }
    >
      <Box className="instance-users-dialog-stack">
        <Box className="instance-users-dialog-panel">
          <Typography className="instance-users-dialog-section-label">
            Team Details
          </Typography>
          <Box className="instance-users-dialog-grid">
            <TextField
              label="Key"
              fullWidth
              size="small"
              value={values.key}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, key: event.target.value }))
              }
              helperText="Use a stable machine key like outside-sales for workflow lookups."
            />
            <TextField
              label="Team Name"
              fullWidth
              size="small"
              value={values.name}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, name: event.target.value }))
              }
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={values.description}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, description: event.target.value }))
              }
            />
            <TextField
              label="Type"
              fullWidth
              size="small"
              value={values.type}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, type: event.target.value }))
              }
            />
          </Box>
        </Box>

        <Box className="instance-users-dialog-panel">
          <Typography className="instance-users-dialog-section-label">
            Members
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            SelectProps={{
              multiple: true,
              value: values.memberUserIds,
              onChange: (event) =>
                setValues((prev) => ({
                  ...prev,
                  memberUserIds: Array.isArray(event.target.value)
                    ? event.target.value.map(Number)
                    : [],
                })),
              renderValue: (selected) =>
                selected
                  .map((userId) => {
                    const member = sortedUsers.find(
                      (candidate) => Number(candidate?.id) === Number(userId)
                    );
                    return member ? getTeamMemberLabel(member) : `User ${userId}`;
                  })
                  .join(", "),
            }}
          >
            {sortedUsers.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    src={getMemberAvatarSrc(member) || undefined}
                    sx={{ width: 28, height: 28 }}
                  >
                    {getMemberInitials(member)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{getMemberDisplayName(member)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getMemberEmail(member) || formatRoleLabel(member?.role)}
                    </Typography>
                  </Box>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
      </Box>
    </UserDialogShell>
  );
};

const LegacyRoutingTeamAssignments = ({
  memberships = [],
  loading = false,
  onChange,
}) => {
  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            key={`routing-skeleton-${index}`}
            variant="rectangular"
            height={72}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Stack>
    );
  }

  if (!memberships.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No routing teams are available for this instance yet.
      </Alert>
    );
  }

  const updateMembership = (teamId, updates) => {
    if (typeof onChange !== "function") return;
    onChange((prev) =>
      prev.map((membership) =>
        Number(membership?.teamId) === Number(teamId)
          ? { ...membership, ...updates }
          : membership
      )
    );
  };

  return (
    <Stack spacing={1.25}>
      {memberships.map((membership) => {
        const assigned = membership?.assigned === true;
        return (
          <Box
            key={membership?.teamId || membership?.teamKey}
            sx={{
              border: "1px solid",
              borderColor: assigned ? "primary.light" : "divider",
              borderRadius: 2,
              p: 1.5,
              backgroundColor: assigned ? "var(--ec-color-primary-soft)" : "background.paper",
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {membership?.teamName || membership?.teamKey || "Routing Team"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {membership?.teamKey || "team"}
                    {membership?.teamDescription ? ` • ${membership.teamDescription}` : ""}
                  </Typography>
                </Box>

                <CheckboxFieldRow
                  checked={assigned}
                  onChange={(checked) =>
                    updateMembership(membership.teamId, {
                      assigned: checked,
                      receivesEmail: checked ? membership.receivesEmail : false,
                      receivesNotifications: checked ? membership.receivesNotifications : false,
                    })
                  }
                  label="Assigned to this routing team"
                />
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Team Role"
                  value={membership?.roleInTeam || "member"}
                  disabled={!assigned}
                  onChange={(event) =>
                    updateMembership(membership.teamId, {
                      roleInTeam: event.target.value,
                    })
                  }
                >
                  {ROUTING_TEAM_ROLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack spacing={0.5}>
                  <CheckboxFieldRow
                    checked={membership?.receivesEmail === true}
                    onChange={(checked) =>
                      updateMembership(membership.teamId, {
                        receivesEmail: checked,
                        assigned: checked ? true : assigned,
                      })
                    }
                    label="Receives Team Emails"
                    helperText="Only enabled memberships with a valid user email are resolved as email recipients."
                  />
                  <CheckboxFieldRow
                    checked={membership?.receivesNotifications === true}
                    onChange={(checked) =>
                      updateMembership(membership.teamId, {
                        receivesNotifications: checked,
                        assigned: checked ? true : assigned,
                      })
                    }
                    label="Receives Team Notifications"
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const LegacyTeamDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  users = [],
  team = null,
}) => {
  const [values, setValues] = useState({
    key: "",
    name: "",
    description: "",
    type: "sales",
    memberUserIds: [],
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setValues({
      key: team?.key || "",
      name: team?.name || "",
      description: team?.description || "",
      type: team?.type || "sales",
      memberUserIds: Array.isArray(team?.memberUserIds) ? team.memberUserIds.map(Number) : [],
    });
    setError("");
  }, [open, team]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((left, right) =>
        `${left?.first_name || ""} ${left?.last_name || ""}`
          .trim()
          .localeCompare(`${right?.first_name || ""} ${right?.last_name || ""}`.trim())
      ),
    [users]
  );

  const handleSubmit = async () => {
    if (!String(values.name || "").trim()) {
      setError("Team name is required.");
      return;
    }

    setError("");
    await onSubmit({
      key: String(values.key || "").trim(),
      name: String(values.name || "").trim(),
      description: String(values.description || "").trim(),
      type: String(values.type || "sales").trim() || "sales",
      memberUserIds: Array.isArray(values.memberUserIds)
        ? values.memberUserIds.map(Number).filter((id) => Number.isFinite(id))
        : [],
    });
  };

  return (
    <UserDialogShell
      open={open}
      onClose={onClose}
      title={team?.id ? "Edit team" : "Create team"}
      subtitle="Create a lightweight team and assign users from this instance."
      actions={
        <>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <UIButton variant="contained" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Saving..." : team?.id ? "Save team" : "Create team"}
          </UIButton>
        </>
      }
    >
      <Box className="instance-users-dialog-stack">
        <Box className="instance-users-dialog-panel">
          <Typography className="instance-users-dialog-section-label">
            Team Details
          </Typography>
          <Box className="instance-users-dialog-grid">
            <TextField
              label="Key"
              fullWidth
              size="small"
              value={values.key}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, key: event.target.value }))
              }
              helperText="Use a stable machine key like outside-sales for workflow lookups."
            />
            <TextField
              label="Team Name"
              fullWidth
              size="small"
              value={values.name}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, name: event.target.value }))
              }
            />
            <TextField
              label="Type"
              fullWidth
              size="small"
              value={values.type}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, type: event.target.value }))
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={values.description}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, description: event.target.value }))
              }
              sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Members"
              value={values.memberUserIds}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const selectedIds = Array.isArray(selected) ? selected.map(Number) : [];
                  const labels = sortedUsers
                    .filter((user) => selectedIds.includes(Number(user.id)))
                    .map(getTeamMemberLabel);
                  return labels.length ? labels.join(", ") : "No members selected";
                },
              }}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  memberUserIds: Array.isArray(event.target.value)
                    ? event.target.value.map(Number)
                    : [],
                }))
              }
              sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}
            >
              {sortedUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={values.memberUserIds.includes(Number(user.id))} size="small" />
                  <ListItemText
                    primary={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email}
                    secondary={user?.email || formatEffectiveRoleLabel(user)}
                  />
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 2.25 }}>
          {error}
        </Alert>
      ) : null}
    </UserDialogShell>
  );
};

void LegacyRoutingTeamAssignments;
void LegacyTeamDialog;

const InstanceUsers = () => {
  const theme = useTheme();
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [instanceUsers, setInstanceUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersPage, setUsersPage] = useState(0);
  const [teamsPage, setTeamsPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [teamsRowsPerPage, setTeamsRowsPerPage] = useState(10);

  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [visibleColumns, setVisibleColumns] = useState(
    () => new Set(USERS_COLUMNS.filter((c) => !c.locked).map((c) => c.id)),
  );
  const [density, setDensity] = useState("comfortable");
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [activeRoleFilter, setActiveRoleFilter] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("active");
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamDialogSubmitting, setTeamDialogSubmitting] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}?archiveStatus=all`,
      );
      setInstanceUsers(response?.data?.listOfUsers || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsData = async () => {
    if (!instanceUniqueId) return;
    setTeamsLoading(true);
    try {
      const response = await fetchTeams({ instanceUniqueId });
      setTeams(Array.isArray(response?.teams) ? response.teams.map(normalizeTeam) : []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "users" && instanceUniqueId) {
      fetchUsersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, instanceUniqueId]);

  const hasArchivingUsers = useMemo(
    () => instanceUsers.some((user) => String(user?.archiveStatus || "").toLowerCase() === "archiving"),
    [instanceUsers],
  );

  useEffect(() => {
    if (activeTab !== "users" || !instanceUniqueId || !hasArchivingUsers) return undefined;
    const timer = window.setInterval(() => {
      fetchUsersData();
    }, 5000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, instanceUniqueId, hasArchivingUsers]);

  useEffect(() => {
    if (activeTab === "teams" && instanceUniqueId) {
      fetchTeamsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, instanceUniqueId]);

  // const filteredUsers = useMemo(() => filterUsers(instanceUsers, searchQuery), [instanceUsers, searchQuery]);

  const filteredUsers = useMemo(() => {
    let result = filterUsers(instanceUsers, searchQuery);
    if (userStatusFilter === "active") result = result.filter((u) => u?.archived !== true);
    if (userStatusFilter === "archived") result = result.filter((u) => u?.archived === true);
    if (activeRoleFilter) {
      result = result.filter(
        (u) =>
          String(u?.role || "").toLowerCase() ===
          activeRoleFilter.toLowerCase(),
      );
    }
    return result;
  }, [instanceUsers, searchQuery, activeRoleFilter, userStatusFilter]);

  const filteredTeams = useMemo(() => filterTeams(teams, teamSearchQuery), [
    teams,
    teamSearchQuery,
  ]);

  const sortedUsers = useMemo(() => {
    if (!sortBy) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      const aVal = String(a[sortBy] ?? "").toLowerCase();
      const bVal = String(b[sortBy] ?? "").toLowerCase();
      return sortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredUsers, sortBy, sortDir]);

  const pagedUsers = useMemo(
    () =>
      sortedUsers.slice(
        usersPage * usersRowsPerPage,
        usersPage * usersRowsPerPage + usersRowsPerPage,
      ),
    [sortedUsers, usersPage, usersRowsPerPage],
  );
  const pagedTeams = useMemo(
    () =>
      filteredTeams.slice(
        teamsPage * teamsRowsPerPage,
        teamsPage * teamsRowsPerPage + teamsRowsPerPage,
      ),
    [filteredTeams, teamsPage, teamsRowsPerPage],
  );

  useEffect(() => {
    setUsersPage(0);
  }, [searchQuery, userStatusFilter]);

  useEffect(() => {
    const lastValidPage = Math.max(
      0,
      Math.ceil(filteredUsers.length / usersRowsPerPage) - 1,
    );
    if (usersPage > lastValidPage) {
      setUsersPage(lastValidPage);
    }
  }, [filteredUsers.length, usersPage, usersRowsPerPage]);

  useEffect(() => {
    setTeamsPage(0);
  }, [teamSearchQuery]);

  const uniqueRoleCount = useMemo(() => {
    const roles = filteredUsers
      .map((user) =>
        String(user?.role || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);
    return new Set(roles).size;
  }, [filteredUsers]);

const MAX_VISIBLE_ROLES = 4;

const activeRoleObject = USER_ROLE_OPTIONS.find(
  (role) => role.value === activeRoleFilter,
);

let visibleRoles = USER_ROLE_OPTIONS.slice(0, MAX_VISIBLE_ROLES);

if (
  activeRoleObject &&
  !visibleRoles.some((r) => r.value === activeRoleObject.value)
) {
  visibleRoles = [
    ...visibleRoles.slice(0, MAX_VISIBLE_ROLES - 1),
    activeRoleObject,
  ];
}

const hiddenRoles = USER_ROLE_OPTIONS.filter(
  (role) =>
    !visibleRoles.some((visibleRole) => visibleRole.value === role.value),
);

  const trackedTeamMembers = useMemo(
    () =>
      filteredTeams.reduce(
        (total, team) => total + Number(team?.memberCount || 0),
        0,
      ),
    [filteredTeams],
  );

  const summaryCards =
    activeTab === "users"
      ? [
          {
            icon: <PeopleAltOutlinedIcon fontSize="small" />,
            label: "Total Users",
            value: instanceUsers.length,
            hint: "All users in this workspace",
          },
          {
            icon: <BadgeRoundedIcon fontSize="small" />,
            label: "Visible Rows",
            value: filteredUsers.length,
            hint: searchQuery
              ? `Matching "${searchQuery}"`
              : "Matches current search",
          },
          {
            icon: <GroupOutlinedIcon fontSize="small" />,
            label: "Roles",
            value: uniqueRoleCount,
            hint: "Roles in the current view",
          },
        ]
      : [
          {
            icon: <GroupOutlinedIcon fontSize="small" />,
            label: "Total Teams",
            value: teams.length,
            hint: "Teams in this workspace",
          },
          {
            icon: <BadgeRoundedIcon fontSize="small" />,
            label: "Visible Rows",
            value: filteredTeams.length,
            hint: teamSearchQuery
              ? `Matching "${teamSearchQuery}"`
              : "Matches current search",
          },
          {
            icon: <PeopleAltOutlinedIcon fontSize="small" />,
            label: "Members",
            value: trackedTeamMembers,
            hint: "Members across visible teams",
          },
        ];

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleEditUserSuccess = async (_response = null, message = "User updated successfully.", severity = "success") => {
    await fetchUsersData();
    showSnackbar(message, severity);
  };

  const handleCreateUserSuccess = async (response) => {
    await fetchUsersData();

    if (
      response?.emailWarning ||
      (response?.accountCreatedEmailRequested && response?.emailSent === false)
    ) {
      showSnackbar("User created, but the email could not be sent.", "warning");
      return;
    }

    if (response?.emailSent) {
      showSnackbar("User created and email sent.");
      return;
    }

    showSnackbar("User created successfully.");
  };

  const handleOpenCreateTeam = async () => {
    if (!instanceUsers.length) {
      await fetchUsersData();
    }
    setSelectedTeam(null);
    setTeamDialogOpen(true);
  };

  const handleOpenEditTeam = async (team) => {
    if (!instanceUsers.length) {
      await fetchUsersData();
    }
    setSelectedTeam(team);
    setTeamDialogOpen(true);
  };

  const handleSaveTeam = async (payload) => {
    setTeamDialogSubmitting(true);
    try {
      if (selectedTeam?.id) {
        await updateTeam(selectedTeam.id, {
          instanceUniqueId,
          ...payload,
        });
        showSnackbar("Team updated successfully");
      } else {
        await createTeam({
          instanceUniqueId,
          ...payload,
        });
        showSnackbar("Team created successfully");
      }
      setTeamDialogOpen(false);
      setSelectedTeam(null);
      await fetchTeamsData();
    } catch (error) {
      console.error("Error saving team:", error);
      showSnackbar(error?.response?.data?.error || "Unable to save team", "error");
    } finally {
      setTeamDialogSubmitting(false);
    }
  };

  const handleDeactivateTeam = async (team) => {
    if (!team?.id) return;
    const confirmed = window.confirm(`Deactivate team "${team.name}"?`);
    if (!confirmed) return;

    try {
      await deactivateTeam(team.id, instanceUniqueId);
      showSnackbar("Team deactivated successfully");
      await fetchTeamsData();
    } catch (error) {
      console.error("Error deactivating team:", error);
      showSnackbar(error?.response?.data?.error || "Unable to deactivate team", "error");
    }
  };

  const renderUserRow = (user) => {
    const avatarSrc = resolveProfileImage(user?.profilePicture);
    const hourlyRate = formatHourlyRate(
      user?.hourly_rate,
      user?.hourly_rate_currency || "CAD",
    );
    const visible = (colId) =>
      visibleColumns.has(colId) ||
      USERS_COLUMNS.find((c) => c.id === colId)?.locked;

    return (
      <TableRow hover key={user.id} className="instance-users-table-row">
        {visible("profile") && (
          <TableCell sx={{ py: currentDensity.py, minWidth: 220 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={avatarSrc}
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: 12,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                }}
              >
                {getUserInitials(user)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography className="instance-users-cell-primary">
                    {[user?.first_name, user?.last_name]
                      .filter(Boolean)
                      .join(" ") || "Unnamed user"}
                  </Typography>
                  {String(user?.archiveStatus || "").toLowerCase() === "archiving"
                    ? <UIStatusChip size="small" label="Archiving" tone="info" />
                    : String(user?.archiveStatus || "").toLowerCase() === "failed"
                      ? <UIStatusChip size="small" label="Archive failed" tone="error" />
                      : user?.archived
                        ? <UIStatusChip size="small" label="Archived" tone="warning" />
                        : null}
                </Stack>
                <Typography className="instance-users-cell-secondary">
                  {user?.email || "—"}
                </Typography>
              </Box>
            </Stack>
          </TableCell>
        )}

        {visible("id") && (
          <TableCell sx={{ py: currentDensity.py, minWidth: 96 }}>
            <Typography className="instance-users-id-pill">
              {user?.id || "—"}
            </Typography>
          </TableCell>
        )}

        {visible("first_name") && (
          <TableCell sx={{ py: currentDensity.py }}>
            <Typography className="instance-users-cell-primary">
              {user?.first_name || "—"}
            </Typography>
          </TableCell>
        )}

        {visible("last_name") && (
          <TableCell sx={{ py: currentDensity.py }}>
            <Typography className="instance-users-cell-primary">
              {user?.last_name || "—"}
            </Typography>
          </TableCell>
        )}

        {visible("email") && (
          <TableCell sx={{ py: currentDensity.py }}>
            {user?.email || "—"}
          </TableCell>
        )}

        {visible("role") && (
          <TableCell sx={{ py: currentDensity.py }}>
            <UIStatusChip label={formatEffectiveRoleLabel(user)} tone="neutral" />
          </TableCell>
        )}

        {visible("hourly_rate") && (
          <TableCell sx={{ py: currentDensity.py }}>
            <Typography className="instance-users-rate-value">
              {hourlyRate || "Not set"}
            </Typography>
            <Typography className="instance-users-rate-meta">
              {user?.hourly_rate_currency || "CAD"}
              {hourlyRate ? " / hr" : ""}
            </Typography>
          </TableCell>
        )}

        {/* Actions always visible locked true */}
        <TableCell align="right" sx={{ py: currentDensity.py }}>
          <Tooltip title="Edit user">
            <UITableActionCell align="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={() => handleOpenEdit(user)}
              >
                Edit
              </Button>
            </UITableActionCell>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const renderTeamRow = (team) => (
    <TableRow hover key={team.id} className="instance-users-table-row">
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[0].minWidth }}
      >
        <Typography className="instance-users-id-pill">
          {team?.id || "-"}
        </Typography>
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[1].minWidth }}
      >
        <Typography className="instance-users-cell-secondary">
          {team?.key || "—"}
        </Typography>
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[2].minWidth }}
      >
        <Typography className="instance-users-cell-primary">
          {team?.name || "Untitled team"}
        </Typography>
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[3].minWidth }}
      >
        <UIStatusChip
          size="small"
          className="instance-users-role-chip"
          label={formatRoleLabel(team?.type || "sales")}
          tone="neutral"
        />
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[4].minWidth }}
      >
        <Typography className="instance-users-cell-secondary">
          {team?.description || "No description available"}
        </Typography>
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[5].minWidth }}
      >
        {Array.isArray(team?.members) && team.members.length ? (
          <Stack spacing={0.9}>
            <UIStatusChip
              size="small"
              className="instance-users-role-chip"
              label={`${team?.memberCount || 0} member${team?.memberCount === 1 ? "" : "s"}`}
              tone="neutral"
            />
            <Stack direction="row" alignItems="center" sx={{ pl: 0.25 }}>
              {team.members.slice(0, 5).map((member, index) => {
                const name = getMemberDisplayName(member);
                const email = getMemberEmail(member);
                return (
                  <Tooltip
                    key={member?.id || `${name}-${email}-${index}`}
                    arrow
                    title={
                      <Box sx={{ py: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {name}
                        </Typography>
                        {email ? (
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {email}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                  >
                    <Avatar
                      src={getMemberAvatarSrc(member)}
                      alt={name}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: 12,
                        ml: index === 0 ? 0 : -0.6,
                        border: `2px solid ${theme.palette.background.paper}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: "text.primary",
                        cursor: "default",
                      }}
                    >
                      {getMemberInitials(member)}
                    </Avatar>
                  </Tooltip>
                );
              })}

              {team.members.length > 5 ? (
                <Tooltip
                  arrow
                  title={
                    <Box sx={{ py: 0.25, maxWidth: 240 }}>
                      {team.members.slice(5).map((member, index) => {
                        const name = getMemberDisplayName(member);
                        const email = getMemberEmail(member);
                        return (
                          <Box
                            key={member?.id || `${name}-${email}-overflow-${index}`}
                            sx={{ mb: index === team.members.slice(5).length - 1 ? 0 : 0.75 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {name}
                            </Typography>
                            {email ? (
                              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                {email}
                              </Typography>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>
                  }
                >
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 12,
                      ml: -0.6,
                      border: `2px solid ${theme.palette.background.paper}`,
                      bgcolor: alpha(theme.palette.text.primary, 0.08),
                      color: "text.primary",
                      cursor: "default",
                    }}
                  >
                    +{team.members.length - 5}
                  </Avatar>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        ) : (
          <Typography className="instance-users-cell-secondary">
            No members
          </Typography>
        )}
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        sx={{ minWidth: TEAMS_COLUMNS[6].minWidth }}
      >
        <UIStatusChip
          size="small"
          className="instance-users-role-chip"
          label={team?.isActive ? "Yes" : "No"}
          tone={team?.isActive ? "success" : "neutral"}
        />
      </TableCell>
      <TableCell
        className="instance-users-table-cell"
        align="right"
        sx={{ minWidth: TEAMS_COLUMNS[7].minWidth }}
      >
        <UITableActionCell align="flex-end">
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon fontSize="small" />}
            onClick={() => handleOpenEditTeam(team)}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={() => handleDeactivateTeam(team)}
          >
            Deactivate
          </Button>
        </UITableActionCell>
      </TableCell>
    </TableRow>
  );

  const renderHeaderCell = (column) => {
    const isUsers = activeTab === "users";
    const isSorted = isUsers && sortBy === column.id;

    return (
      <TableCell
        key={column.id}
        align={column.align || "left"}
        onClick={
          isUsers && column.sortable ? () => handleSort(column.id) : undefined
        }
        sx={{
          minWidth: column.minWidth,
          px: 2,
          py: 1.25,
          backgroundColor: "var(--ec-table-head-bg-strong) !important",
          color: isSorted
            ? "var(--ec-table-head-text-inverse)"
            : "var(--ec-table-head-text-muted)",
          fontWeight: 700,
          fontSize: "0.72rem",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          borderBottom: "none",
          cursor: isUsers && column.sortable ? "pointer" : "default",
          whiteSpace: "nowrap",
          userSelect: "none",
          "&:hover":
            isUsers && column.sortable
              ? {
                  backgroundColor: "var(--ec-table-head-bg) !important",
                  color: "var(--ec-table-head-text)",
                }
              : {},
          transition: "background 0.15s ease, color 0.15s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {column.label}
          {isUsers && column.sortable && (
            <Box
              sx={{
                display: "inline-flex",
                ml: 0.25,
                opacity: isSorted ? 1 : 0.35,
              }}
            >
              {isSorted ? (
                sortDir === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                )
              ) : (
                <UnfoldMoreIcon sx={{ fontSize: 13 }} />
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    );
  };

  const renderEmptyRow = (message, hint, colSpan) => (
    <TableRow>
      <TableCell className="instance-users-table-empty" colSpan={colSpan}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {message}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const densitySettings = {
    compact: { py: 0.75, fontSize: "0.8125rem" },
    comfortable: { py: 1.5, fontSize: "0.875rem" },
    spacious: { py: 2.5, fontSize: "0.9375rem" },
  };

  const currentDensity =
    densitySettings[density] || densitySettings.comfortable;

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const toggleColVisibility = (colId) =>
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      next.has(colId) ? next.delete(colId) : next.add(colId);
      return next;
    });

  const handleSort = (colId) => {
    if (sortBy === colId) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortBy("");
        setSortDir("asc");
      }
    } else {
      setSortBy(colId);
      setSortDir("asc");
    }
  };

  const exportCSV = () => {
    const rows = [USERS_EXPORT_COLS.map((c) => c.label).join(",")];
    sortedUsers.forEach((u) => {
      rows.push(
        USERS_EXPORT_COLS.map(
          (c) => `"${String(u[c.id] ?? "").replace(/"/g, '""')}"`,
        ).join(","),
      );
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" }),
    );
    a.download = `users_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    showSnackbar("CSV exported successfully");
  };

  const exportExcel = () => {
    const rows = [USERS_EXPORT_COLS.map((c) => c.label).join("\t")];
    sortedUsers.forEach((u) => {
      rows.push(USERS_EXPORT_COLS.map((c) => String(u[c.id] ?? "")).join("\t"));
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob([rows.join("\n")], { type: "application/vnd.ms-excel" }),
    );
    a.download = `users_${new Date().toISOString().split("T")[0]}.xls`;
    a.click();
    showSnackbar("Excel exported successfully");
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Users Report", 14, 15);
    doc.setFontSize(9);
    doc.text(
      `Generated: ${new Date().toLocaleString("en-IN")}   Total: ${
        filteredUsers.length
      }`,
      14,
      22,
    );
    autoTable(doc, {
      head: [USERS_EXPORT_COLS.map((c) => c.label)],
      body: sortedUsers.map((u) =>
        USERS_EXPORT_COLS.map((c) => String(u[c.id] ?? "")),
      ),
      startY: 28,
      styles: { fontSize: 7, cellPadding: 1.5, overflow: "ellipsize" },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`users_${new Date().toISOString().split("T")[0]}.pdf`);
    showSnackbar("PDF exported successfully");
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setActiveRoleFilter("");
    setUserStatusFilter("active");
    setSearchQuery("");
    setTeamSearchQuery("");
  };

  const hasActiveFilters =
    activeTab === "users"
      ? !!searchQuery || !!activeRoleFilter || userStatusFilter !== "active"
      : !!teamSearchQuery;

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />

      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)",
            overflow: "hidden",
          }}
        >
          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              boxShadow:
                "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
            }}
          >
            <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: "text.primary",
                      lineHeight: 1,
                      fontSize: "1rem",
                    }}
                  >
                    Users &amp; Teams
                  </Typography>

                  <Chip
                    label={
                      activeTab === "users"
                        ? `${instanceUsers.length} total`
                        : `${teams.length} total`
                    }
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  <Stack direction="row" spacing={0.5}>
                    {[
                      { key: "users", label: "Users" },
                      { key: "teams", label: "Teams" },
                    ].map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        onClick={() => handleTabChange(key)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 26,
                          backgroundColor:
                            activeTab === key
                              ? "var(--ec-color-primary)"
                              : "var(--ec-status-neutral-bg)",
                          color:
                            activeTab === key
                              ? "var(--ec-color-surface)"
                              : "var(--ec-status-neutral-text)",
                          border:
                            activeTab === key
                              ? "1px solid transparent"
                              : "1px solid var(--ec-status-neutral-border)",
                          transition: "all 0.15s ease",
                          "& .MuiChip-label": { px: "10px" },
                          "&:hover": {
                            backgroundColor:
                              activeTab === key
                                ? "var(--ec-color-primary-dark)"
                                : "var(--ec-color-surface-muted)",
                          },
                        }}
                      />
                    ))}
                  </Stack>

                  {activeTab === "users" && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center" }} />
                      <Stack direction="row" spacing={0.5}>
                        {[
                          { key: "active", label: "Active" },
                          { key: "archived", label: "Archived" },
                          { key: "all", label: "All" },
                        ].map(({ key, label }) => (
                          <Chip
                            key={key}
                            label={label}
                            size="small"
                            onClick={() => setUserStatusFilter(key)}
                            variant={userStatusFilter === key ? "filled" : "outlined"}
                            color={userStatusFilter === key ? "primary" : "default"}
                            sx={{ fontWeight: 600, fontSize: "0.72rem", height: 26 }}
                          />
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* {activeTab === "users" && (
                    <>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: 20, alignSelf: "center" }}
                      />
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {USER_ROLE_OPTIONS.map(({ value, label }) => (
                          <UIStatusChip
                            key={value}
                            label={label}
                            tone="neutral"
                            selected={activeRoleFilter === value}
                            onClick={() =>
                              setActiveRoleFilter((prev) =>
                                prev === value ? "" : value,
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          />
                        ))}
                      </Stack>
                    </>
                  )} */}
                  {activeTab === "users" && (
  <>
    <Divider
      orientation="vertical"
      flexItem
      sx={{ height: 20, alignSelf: "center" }}
    />

    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
    >
      {visibleRoles.map(({ value, label }) => (
        // <UIStatusChip
        //   key={value}
        //   label={label}
        //   tone="neutral"
        //   selected={activeRoleFilter === value}
        //   onClick={() =>
        //     setActiveRoleFilter((prev) =>
        //       prev === value ? "" : value
        //     )
        //   }
        //   sx={{ cursor: "pointer" }}
        // />
        <UIStatusChip
  key={value}
  label={label}
  tone="neutral"
  selected={activeRoleFilter === value}
  onClick={() =>
    setActiveRoleFilter((prev) =>
      prev === value ? "" : value
    )
  }
  sx={{
    cursor: "pointer",

    ...(activeRoleFilter === value && {
      bgcolor: "primary.main",
      borderColor: "primary.main",
      color: "#fff",

      "& .MuiChip-label": {
        color: "#fff",
        fontWeight: 600,
      },
    }),
  }}
/>
      ))}

      {hiddenRoles.length > 0 && (
        <>
          {/* <Chip
            label={`More (${hiddenRoles.length})`} */}
            <Chip
  label={`More (${hiddenRoles.length})`}
  color={
    hiddenRoles.some(
      (role) => role.value === activeRoleFilter,
    )
      ? "primary"
      : "default"
  }
            size="small"
            onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
            sx={{
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 26,
            }}
          />

          <Menu
            anchorEl={roleMenuAnchor}
            open={Boolean(roleMenuAnchor)}
            onClose={() => setRoleMenuAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 220,
                  borderRadius: 2,
                },
              },
            }}
          >
            {hiddenRoles.map(({ value, label }) => (
              <MenuItem
                key={value}
                selected={activeRoleFilter === value}
                  sx={{
    fontWeight:
      activeRoleFilter === value ? 700 : 400,
  }}
                onClick={() => {
                  setActiveRoleFilter((prev) =>
                    prev === value ? "" : value
                  );
                  setRoleMenuAnchor(null);
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  </>
)}
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  {activeTab === "users" && (
                    <>
                    <Tooltip title="Export data">
                      <IconButton
                        size="small"
                        onClick={(e) => setExportAnchorEl(e.currentTarget)}
                        disabled={sortedUsers.length === 0}
                        sx={{ color: "text.secondary" }}
                      >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={exportAnchorEl}
                      open={Boolean(exportAnchorEl)}
                      onClose={() => setExportAnchorEl(null)}
                      slotProps={{
                        paper: { sx: { minWidth: 190, borderRadius: 2 } },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 0.5,
                          display: "block",
                          color: "text.disabled",
                        }}
                      >
                        Export as
                      </Typography>
                      <Divider sx={{ mb: 0.5 }} />
                      <MenuItem
                        onClick={() => {
                          exportCSV();
                          setExportAnchorEl(null);
                        }}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemIcon>
                          <FileDownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>CSV</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          exportExcel();
                          setExportAnchorEl(null);
                        }}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemIcon>
                          <TableChartIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Excel</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          exportPDF();
                          setExportAnchorEl(null);
                        }}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemIcon>
                          <PictureAsPdfIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>PDF</ListItemText>
                      </MenuItem>
                    </Menu>

                    <Tooltip title="Show / hide columns">
                      <IconButton
                        size="small"
                        onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                        sx={{ color: "text.secondary" }}
                      >
                        <ViewColumnIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={columnMenuAnchor}
                      open={Boolean(columnMenuAnchor)}
                      onClose={() => setColumnMenuAnchor(null)}
                      slotProps={{
                        paper: { sx: { minWidth: 210, borderRadius: 2 } },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 0.5,
                          display: "block",
                          color: "text.disabled",
                        }}
                      >
                        Columns
                      </Typography>
                      <Divider sx={{ mb: 0.5 }} />
                      {USERS_COLUMNS.filter((c) => !c.locked).map((col) => (
                        <MenuItem
                          key={col.id}
                          onClick={() => toggleColVisibility(col.id)}
                          sx={{ py: 0.4 }}
                        >
                          <Checkbox
                            size="small"
                            checked={visibleColumns.has(col.id)}
                            sx={{
                              p: 0.5,
                              mr: 0.5,
                              color: "primary.main",
                              "&.Mui-checked": { color: "primary.main" },
                            }}
                          />
                          <ListItemText
                            primaryTypographyProps={{ fontSize: "0.875rem" }}
                          >
                            {col.label}
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>

                    <Tooltip title="Row density">
                      <IconButton
                        size="small"
                        onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
                        sx={{ color: "text.secondary" }}
                      >
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={densityMenuAnchor}
                      open={Boolean(densityMenuAnchor)}
                      onClose={() => setDensityMenuAnchor(null)}
                      slotProps={{
                        paper: { sx: { minWidth: 180, borderRadius: 2 } },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 0.5,
                          display: "block",
                          color: "text.disabled",
                        }}
                      >
                        Row density
                      </Typography>
                      {["compact", "comfortable", "spacious"].map((d) => (
                        <MenuItem
                          key={d}
                          onClick={() => {
                            setDensity(d);
                            setDensityMenuAnchor(null);
                          }}
                          sx={{ py: 0.75, textTransform: "capitalize" }}
                        >
                          <Checkbox
                            size="small"
                            checked={density === d}
                            sx={{
                              p: 0.5,
                              mr: 0.5,
                              color: "primary.main",
                              "&.Mui-checked": { color: "primary.main" },
                            }}
                          />
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                            }}
                          >
                            {d}
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                    </>
                  )}

                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (activeTab === "users") fetchUsersData();
                        else fetchTeamsData();
                        showSnackbar("Data refreshed");
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 0.5, height: 24, alignSelf: "center" }}
                  />

                  {activeTab === "users" ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => setModalOpen(true)}
                      sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        height: 34,
                        px: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      New User
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      // disabled
                      onClick={handleOpenCreateTeam}
                      sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        height: 34,
                        px: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      New Team
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ mt: 1.75, mb: 0 }} />

              <Stack
                direction="row"
                spacing={1.5}
                mt={1.75}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <TextField
                  value={activeTab === "users" ? searchQuery : teamSearchQuery}
                  onChange={(e) =>
                    activeTab === "users"
                      ? setSearchQuery(e.target.value)
                      : setTeamSearchQuery(e.target.value)
                  }
                  placeholder={
                    activeTab === "users"
                      ? "Search by name, email, or role…"
                      : "Search teams…"
                  }
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      activeTab === "users" ? searchQuery : teamSearchQuery
                    ) ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            activeTab === "users"
                              ? setSearchQuery("")
                              : setTeamSearchQuery("")
                          }
                          edge="end"
                        >
                          <ClearRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    flex: 2,
                    minWidth: 260,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg, transparent)",
                    },
                  }}
                />

                {(activeTab === "users" ? searchQuery : teamSearchQuery) && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ClearRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={() =>
                      activeTab === "users"
                        ? setSearchQuery("")
                        : setTeamSearchQuery("")
                    }
                    sx={{
                      height: 40,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "text.secondary",
                      px: 1.5,
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "var(--ec-status-error-bg)",
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}

                <UIStatusChip
                  size="small"
                  tone="neutral"
                  label={
                    activeTab === "users"
                      ? `${filteredUsers.length} visible`
                      : `${filteredTeams.length} visible`
                  }
                />
                <UIStatusChip
                  size="small"
                  tone="neutral"
                  label={
                    activeTab === "users"
                      ? `${uniqueRoleCount} roles`
                      : `${trackedTeamMembers} members`
                  }
                />
              </Stack>

              {hasActiveFilters && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  mt={1.25}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600, mr: 0.25 }}
                  >
                    Active filters
                  </Typography>

                  {(activeTab === "users" ? searchQuery : teamSearchQuery) && (
                    <Chip
                      label={`Search: ${activeTab === "users" ? searchQuery : teamSearchQuery}`}
                      size="small"
                      onDelete={() =>
                        activeTab === "users" ? setSearchQuery("") : setTeamSearchQuery("")
                      }
                      sx={{
                        backgroundColor: "var(--ec-status-info-bg)",
                        color: "var(--ec-status-info-text)",
                        border: "1px solid var(--ec-status-info-border)",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                      }}
                    />
                  )}

                  {activeRoleFilter && (
                    <UIStatusChip
                      label={formatRoleLabel(activeRoleFilter)}
                      tone="neutral"
                      onDelete={() => setActiveRoleFilter("")}
                    />
                  )}

                  <Chip
                    label="Clear all"
                    size="small"
                    onClick={() => {
                      if (activeTab === "users") {
                        setSearchQuery("");
                        setActiveRoleFilter("");
                        setUserStatusFilter("active");
                      } else {
                        setTeamSearchQuery("");
                      }
                    }}
                    deleteIcon={<ClearRoundedIcon />}
                    sx={{
                      backgroundColor: "var(--ec-status-error-bg)",
                      color: "var(--ec-status-error-text)",
                      border: "1px solid var(--ec-status-error-border)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
              )}



            </UITableToolbar>


  {activeTab === "teams" && (
    <Alert severity="info" sx={{ mx: 2, mt: 1, mb: 1, borderRadius: 2 }}>
      Teams are scoped to this instance and currently support lightweight name, type, description, and member management.
    </Alert>
  )}

 
  <TableContainer
    sx={{
      flex: 1,
      overflow: "auto",
      minHeight: 0,
      //  borderRadius: 0,
                borderRadius: "0 !important",
      "&::-webkit-scrollbar": { width: 6, height: 6 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--ec-color-border-strong)",
        borderRadius: 99,
      },
      "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    }}
  >
    <Table
      stickyHeader
      size="small"
      sx={{ minWidth: activeTab === "users" ? 1080 : 820, width: "100%" }}
    >

 
      <TableHead>
        <TableRow>
          {(activeTab === "users"
            ? USERS_COLUMNS.filter((c) => visibleColumns.has(c.id) || c.locked)
            : TEAMS_COLUMNS
          ).map((col, idx, arr) => (
            <TableCell
              key={col.id}
              align={col.align || "left"}
              onClick={activeTab === "users" && col.sortable ? () => handleSort(col.id) : undefined}
              sx={{
                minWidth: col.minWidth,
                px: 2,
                py: 1.25,
                backgroundColor: "var(--ec-table-head-bg-strong) !important",
                color: sortBy === col.id
                  ? "var(--ec-table-head-text-inverse)"
                  : "var(--ec-table-head-text-muted)",
                fontWeight: 700,
                fontSize: "0.72rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderBottom: "none",
                cursor: activeTab === "users" && col.sortable ? "pointer" : "default",
                userSelect: "none",
                whiteSpace: "nowrap",
                position: "sticky",
                top: 0,
                zIndex: 2,
                transition: "all 0.15s",
                "&:hover": activeTab === "users" && col.sortable
                  ? { backgroundColor: "var(--ec-table-head-bg-hover) !important", color: "var(--ec-table-head-text-inverse)" }
                  : {},
                ...(idx === 0 && { borderTopLeftRadius: "9px" }),
                ...(idx === arr.length - 1 && { borderTopRightRadius: "9px" }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {col.label}
                {activeTab === "users" && col.sortable && (
                  <Box sx={{ display: "inline-flex", ml: 0.25, opacity: sortBy === col.id ? 1 : 0.4 }}>
                    {sortBy === col.id
                      ? sortDir === "asc"
                        ? <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                      : <UnfoldMoreIcon sx={{ fontSize: 13 }} />
                    }
                  </Box>
                )}
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
 
      <TableBody
        sx={{
          "& .MuiTableCell-root": { borderBottom: "1px solid var(--ec-color-border)" },
          "& .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: "none" },
          "& .MuiTableRow-root:hover": { backgroundColor: "var(--ec-color-primary-soft)" },
        }}
      >
        {activeTab === "users"
          ? loading
            ? Array.from({ length: usersRowsPerPage }).map((_, i) => (
                <TableRow key={i}>
                  {USERS_COLUMNS.filter((c) => visibleColumns.has(c.id) || c.locked).map((col) => (
                    <TableCell key={col.id} sx={{ px: 2, py: currentDensity.py }}>
                      <Skeleton
                        variant="rectangular"
                        height={18}
                        sx={{ borderRadius: 1, opacity: Math.max(0.15, 1 - i * 0.09) }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : pagedUsers.length > 0
              ? pagedUsers.map(renderUserRow)
              : renderEmptyRow(
                  "No users match your search",
                  "Try a different name, email, or role.",
                  USERS_COLUMNS.filter((c) => visibleColumns.has(c.id) || c.locked).length
                )
          : teamsLoading
            ? Array.from({ length: teamsRowsPerPage }).map((_, i) => (
                <TableRow key={`team-skeleton-${i}`}>
                  {TEAMS_COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ px: 2, py: currentDensity.py }}>
                      <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : pagedTeams.length > 0
              ? pagedTeams.map(renderTeamRow)
              : renderEmptyRow(
                  "No teams match your search",
                  "Create a team or try a different team name or keyword.",
                  TEAMS_COLUMNS.length
                )
        }
      </TableBody>

    </Table>
  </TableContainer>

 
  <Box
    sx={{
      px: 2,
      py: 0,
      backgroundColor: "background.paper",
      borderTop: "1px solid",
      borderColor: "divider",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 52,
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: "var(--ec-color-text-muted)", fontSize: "0.8125rem", pl: 1 }}
    >
      {activeTab === "users"
        ? filteredUsers.length === 0
          ? "No results"
          : `Showing ${usersPage * usersRowsPerPage + 1}–${Math.min((usersPage + 1) * usersRowsPerPage, filteredUsers.length)} of ${filteredUsers.length} user${filteredUsers.length !== 1 ? "s" : ""}`
        : filteredTeams.length === 0
          ? "No results"
          : `Showing ${teamsPage * teamsRowsPerPage + 1}–${Math.min((teamsPage + 1) * teamsRowsPerPage, filteredTeams.length)} of ${filteredTeams.length} team${filteredTeams.length !== 1 ? "s" : ""}`
      }
    </Typography>

    <TablePagination
      component="div"
      count={activeTab === "users" ? filteredUsers.length : filteredTeams.length}
      page={activeTab === "users" ? usersPage : teamsPage}
      onPageChange={(_, next) =>
        activeTab === "users" ? setUsersPage(next) : setTeamsPage(next)
      }
      rowsPerPage={activeTab === "users" ? usersRowsPerPage : teamsRowsPerPage}
      onRowsPerPageChange={(e) => {
        const val = Number(e.target.value);
        if (activeTab === "users") {
          setUsersRowsPerPage(val);
          setUsersPage(0);
        } else {
          setTeamsRowsPerPage(val);
          setTeamsPage(0);
        }
      }}
      rowsPerPageOptions={activeTab === "users" ? [10, 25, 50, 100] : [10, 25, 50]}
      showFirstButton
      showLastButton
      sx={{
        border: 0,
        "& .MuiTablePagination-toolbar": { minHeight: 52, px: 0 },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
          fontSize: "0.8125rem",
          color: "var(--ec-color-text-muted) !important",
          mb: 0,
        },
      }}
    />
    </Box>

            {/* {activeTab === "teams" && (
              <Alert
                severity="info"
                sx={{ mx: 2, mt: 1.5, mb: 0, borderRadius: 2 }}
              >
                This section is currently in preview while team management
                features are being finalized.
              </Alert>
            )}

            {activeTab === "users" ? (
              <UITableShell
                columns={USERS_COLUMNS.filter(
                  (c) => visibleColumns.has(c.id) || c.locked,
                )}
                minWidth={1080}
                renderHeaderCell={renderHeaderCell}
                bodySx={{
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: "var(--ec-color-primary-soft)",
                  },
                }}
                footer={
                  <UITableFooter
                    count={filteredUsers.length}
                    page={usersPage}
                    rowsPerPage={usersRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    summaryLabel="users"
                    onPageChange={(_, nextPage) => setUsersPage(nextPage)}
                    onRowsPerPageChange={(event) => {
                      setUsersRowsPerPage(Number(event.target.value));
                      setUsersPage(0);
                    }}
                    sx={{ px: 1.75 }}
                    paginationProps={{
                      showFirstButton: true,
                      showLastButton: true,
                    }}
                  />
                }
              >
                {loading
                  ? Array(usersRowsPerPage)
                      .fill(null)
                      .map((_, i) => (
                        <TableRow key={i}>
                          {USERS_COLUMNS.filter(
                            (c) => visibleColumns.has(c.id) || c.locked,
                          ).map((col) => (
                            <TableCell
                              key={col.id}
                              sx={{ py: currentDensity.py, px: 2 }}
                            >
                              <Skeleton
                                variant="rectangular"
                                height={16}
                                sx={{
                                  borderRadius: 1,
                                  opacity: Math.max(0.12, 1 - i * 0.09),
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                  : pagedUsers.length > 0
                  ? pagedUsers.map(renderUserRow)
                  : renderEmptyRow(
                      "No users match your search",
                      "Try a different name, email, or role.",
                      USERS_COLUMNS.filter(
                        (c) => visibleColumns.has(c.id) || c.locked,
                      ).length,
                    )}
              </UITableShell>
            ) : (
              <UITableShell
                columns={TEAMS_COLUMNS}
                minWidth={820}
                renderHeaderCell={renderHeaderCell}
                bodySx={{
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: "var(--ec-color-primary-soft)",
                  },
                }}
                footer={
                  <UITableFooter
                    count={filteredTeams.length}
                    page={teamsPage}
                    rowsPerPage={teamsRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    summaryLabel="teams"
                    onPageChange={(_, nextPage) => setTeamsPage(nextPage)}
                    onRowsPerPageChange={(event) => {
                      setTeamsRowsPerPage(Number(event.target.value));
                      setTeamsPage(0);
                    }}
                    sx={{ px: 1.75 }}
                    paginationProps={{
                      showFirstButton: true,
                      showLastButton: true,
                    }}
                  />
                }
              >
                {pagedTeams.length > 0
                  ? pagedTeams.map(renderTeamRow)
                  : renderEmptyRow(
                      "No teams match your search",
                      "Try a different team name or keyword.",
                      TEAMS_COLUMNS.length,
                    )}
              </UITableShell>
            )} */}
          </UITableShell>
        </Box>

        <CreateUserModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          authState={authState}
          instanceUniqueId={instanceUniqueId}
          onSuccess={handleCreateUserSuccess}
        />
        <EditUserModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          user={selectedUser}
          authState={authState}
          availableUsers={instanceUsers}
          instanceUniqueId={instanceUniqueId}
          onSuccess={handleEditUserSuccess}
        />

        <TeamDialog
          open={teamDialogOpen}
          onClose={() => {
            setTeamDialogOpen(false);
            setSelectedTeam(null);
          }}
          team={selectedTeam}
          users={instanceUsers}
          submitting={teamDialogSubmitting}
          onSubmit={handleSaveTeam}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            variant="filled"
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default InstanceUsers;
